<template>
    <div class="email-modal-overlay">
        <div class="email-modal-card">
            <div class="email-modal-header">
                <div>
                    <h3>Enviar laudo por e-mail</h3>
                    <p>Confirme os destinatários para envio do laudo.</p>
                </div>

                <button
                    type="button"
                    class="email-modal-close"
                    @click="emit('close')"
                    title="Fechar"
                >
                    ×
                </button>
            </div>

            <div class="email-modal-body">
                <label class="email-modal-check">
                    <input
                        type="checkbox"
                        v-model="usarEnvolvidos"
                        class="email-modal-checkbox"
                    >
                    <span>Enviar para os envolvidos cadastrados</span>
                </label>

                <div v-if="usarEnvolvidos" class="email-modal-info">
                    O sistema enviará o e-mail para os envolvidos vinculados ao registro.
                </div>

                <div v-else class="email-modal-field">
                    <label class="email-modal-label">E-mails para envio</label>

                    <textarea
                        v-model="emailsManual"
                        class="email-modal-textarea"
                        rows="5"
                        placeholder="Informe um ou mais e-mails separados por vírgula, ponto e vírgula ou quebra de linha"
                    ></textarea>
                </div>

                <div v-if="msgErro" class="email-modal-alert">
                    {{ msgErro }}
                </div>
            </div>

            <div class="email-modal-footer">
                <button
                    type="button"
                    class="email-modal-btn email-modal-btn-cancel"
                    @click="emit('close')"
                >
                    Cancelar
                </button>

                <button
                    type="button"
                    class="email-modal-btn email-modal-btn-confirm"
                    @click="confirmarEnvio"
                >
                    Confirmar envio
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';

const emit = defineEmits(['close', 'confirm']);

const usarEnvolvidos = ref(true);
const emailsManual = ref('');
const msgErro = ref(null);

function extrairEmails(texto) {
    return texto
        .split(/[\n,;]+/)
        .map(email => email.trim())
        .filter(email => email);
}

function emailValido(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function confirmarEnvio() {
    msgErro.value = null;

    if (usarEnvolvidos.value) {
        emit('confirm', {
            usarEnvolvidos: true,
            emails: ''
        });
        return;
    }

    const emails = extrairEmails(emailsManual.value);

    if (!emails.length) {
        msgErro.value = 'Informe pelo menos um e-mail para envio.';
        return;
    }

    const invalidos = emails.filter(email => !emailValido(email));

    if (invalidos.length) {
        msgErro.value = `E-mail inválido: ${invalidos.join(', ')}`;
        return;
    }

    emit('confirm', {
        usarEnvolvidos: false,
        emails: emails.join(';')
    });
}
</script>

<style scoped>
.email-modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;

    display: flex;
    align-items: center;
    justify-content: center;

    padding: 15px;
    background: rgba(0, 0, 0, 0.45);
}

.email-modal-card {
    width: 100%;
    max-width: 540px;

    background: #ffffff;
    border: 1px solid #d7d7d7;
    border-radius: 8px;
    overflow: hidden;

    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.18);
}

.email-modal-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;

    padding: 15px 18px;
    background: #f8f9fa;
    border-bottom: 1px solid #e0e0e0;

    text-align: left;
}

.email-modal-header h3 {
    margin: 0;
    font-size: 18px;
    color: #222;
}

.email-modal-header p {
    margin: 4px 0 0 0;
    font-size: 13px;
    color: #666;
}

.email-modal-close {
    width: 30px;
    height: 30px;
    min-width: 30px;

    padding: 0;
    border: none;
    border-radius: 50%;
    background: transparent;

    color: #333;
    font-size: 24px;
    line-height: 26px;
    cursor: pointer;
}

.email-modal-close:hover {
    background: #e5e5e5;
    color: #000;
}

.email-modal-body {
    padding: 18px;
    text-align: left;
}

.email-modal-check {
    display: flex;
    align-items: center;
    gap: 8px;

    margin-bottom: 12px;

    font-weight: 600;
    color: #333;
    cursor: pointer;
}

.email-modal-checkbox {
    width: 16px;
    height: 16px;
    min-height: auto;
    cursor: pointer;
}

.email-modal-info {
    padding: 10px 12px;
    border: 1px solid #cfe5e2;
    border-radius: 6px;
    background: #f2fbfa;

    color: #25413e;
    font-size: 14px;
}

.email-modal-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.email-modal-label {
    font-weight: bold;
    font-size: 0.9em;
    opacity: 0.75;
}

.email-modal-textarea {
    width: 100%;
    min-height: 120px;

    padding: 8px;
    border: 1px solid #cfcfcf;
    border-radius: 5px;
    box-sizing: border-box;

    font-family: inherit;
    font-size: 14px;

    resize: vertical;
}

.email-modal-textarea:focus {
    outline: none;
    border-color: #4a827c;
    box-shadow: 0 0 0 2px rgba(74, 130, 124, 0.22);
}

.email-modal-alert {
    display: flex;
    align-items: flex-start;
    gap: 8px;

    margin-top: 12px;
    padding: 8px 10px;

    background-color: #fff8e1;
    color: #8a4b00;

    border: 3px solid #f59e0b;
    border-left: 6px solid #f59e0b;
    border-radius: 6px;

    font-weight: 500;
    font-size: 14px;
    text-align: left;
}

.email-modal-alert::before {
    content: "⚠️";
}

.email-modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;

    padding: 15px 18px;
    background: #f8f9fa;
    border-top: 1px solid #e0e0e0;
}

.email-modal-btn {
    width: 120px;
    padding: 6px 8px;

    border: none;
    border-radius: 4px;

    font-size: 14px;
    cursor: pointer;
}

.email-modal-btn-cancel {
    background-color: #eff6f5;
    color: #25413e;
    box-shadow: 0 0 0 1px #25413e inset;
}

.email-modal-btn-cancel:hover {
    background-color: #aed0cd;
    color: #0e251b;
    box-shadow: 0 0 0 2px #13201f inset;
}

.email-modal-btn-confirm {
    background-color: #4a29ed;
    color: #ffffff;
    font-weight: bold;
}

.email-modal-btn-confirm:hover {
    background-color: #0f085f;
    color: #ffffff;
}
</style>